import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";

// --- API Configuration ---
const api = axios.create({
  baseURL: "https://api1-vapt-report.infosharesystems.io/vulnerability-assessments",
});
api.defaults.withCredentials = true;

const apiEndpoints = {
  getTesters: "/users/testers",
  getTeamLeads: "/users/teamleads",
  getManagers: "/users/managers",
  getAssistantManagers: "/users/assistant-managers",
};
// --- End of API Configuration ---

// --- TEMPLATE FOR DYNAMIC CONTENT ---
const PRIVACY_TEMPLATE =
  "The information contained herein is considered privileged, confidential and the property of Varutra and {clientName}. This internal report, including any reproduction portions of this report, contains conclusions and analysis that reflect the information available to Varutra prior to the completion date of the work. Upon delivery, Varutra has not continued to monitor, nor does the report reflect, any later alterations that may have been made to the subject matter of this report.";

const NewAssessmentForm = ({ onSave, onBack, assessmentToEdit }) => {
  // State to manage form data (remains flat for easy management)
  const [formData, setFormData] = useState({
    clientName: "",
    activityName: "",
    scopeDetails: "",
    outOfScope: "",
    description: "",
    version: "1.0",
    privacy_of_content: PRIVACY_TEMPLATE.replace(
      "{clientName}",
      "{Select a Client}"
    ),
    assessmentStartDate: "",
    assessmentEndDate: "",
    reportReleaseDate: "",
    preparedBy: [],
    reviewedBy: "",
    approvedBy: "",
    sdm: "",
    status: {
      name: "In Progress",
    },
  });

  // State variables to store fetched user lists
  const [testers, setTesters] = useState([]);
  const [teamLeads, setTeamLeads] = useState([]);
  const [managers, setManagers] = useState([]);
  const [assistantManagers, setAssistantManagers] = useState([]);
  const navigate = useNavigate();

  // Effect to fetch users by role from the API
  useEffect(() => {
    const fetchUsersByRole = async (url, setter) => {
      try {
        const response = await api.get(url);
        setter(response.data);
      } catch (error) {
        console.error(`Error fetching from ${url}:`, error);
      }
    };

    fetchUsersByRole(apiEndpoints.getTesters, setTesters);
    fetchUsersByRole(apiEndpoints.getTeamLeads, setTeamLeads);
    fetchUsersByRole(apiEndpoints.getManagers, setManagers);
    fetchUsersByRole(apiEndpoints.getAssistantManagers, setAssistantManagers);
  }, []);

  // Effect to populate form when editing an existing assessment
  useEffect(() => {
    if (assessmentToEdit) {
      const latestVersion = assessmentToEdit.versions?.[0] || {}; // Get first version
      const preparedByArray =
        typeof latestVersion.preparedBy === "string"
          ? latestVersion.preparedBy.split(",").map((name) => name.trim())
          : latestVersion.preparedBy || [];

      setFormData({
        clientName: assessmentToEdit.clientName || "",
        activityName: assessmentToEdit.activityName || "",
        scopeDetails: assessmentToEdit.scopeDetails || "",
        outOfScope: assessmentToEdit.outOfScope || "",
        privacy_of_content: assessmentToEdit.privacy_of_content || "",
        assessmentStartDate:
          assessmentToEdit.assessmentStartDate?.split("T")[0] || "",
        assessmentEndDate:
          assessmentToEdit.assessmentEndDate?.split("T")[0] || "",
        status: assessmentToEdit.status || { name: "" },
        // Version-specific fields
        description: latestVersion.description || "",
        version: latestVersion.versionId || "1.0",
        reportReleaseDate: latestVersion.reportReleaseDate?.split("T")[0] || "",
        preparedBy: preparedByArray,
        reviewedBy: latestVersion.reviewedBy || "",
        approvedBy: latestVersion.approvedBy || "",
        sdm: latestVersion.sdm || "",
      });
    } else {
      // Reset form for new assessment
      setFormData({
        clientName: "",
        activityName: "",
        scopeDetails: "",
        outOfScope: "",
        description: "",
        version: "1.0",
        privacy_of_content: PRIVACY_TEMPLATE.replace(
          "{clientName}",
          "{Select a Client}"
        ),
        assessmentStartDate: "",
        assessmentEndDate: "",
        reportReleaseDate: "",
        preparedBy: [],
        reviewedBy: "",
        approvedBy: "",
        sdm: "",
        status: { name: "In Progress" },
      });
    }
  }, [assessmentToEdit]);

  // Effect to dynamically update privacy content
  useEffect(() => {
    const client = formData.clientName || "{Select a Client}";
    const newPrivacyText = PRIVACY_TEMPLATE.replace("{clientName}", client);

    setFormData((prev) => ({
      ...prev,
      privacy_of_content: newPrivacyText,
    }));
  }, [formData.clientName]);

  // Handle changes for standard form inputs
  const handleChange = (e) => {
    const { id, value } = e.target;
    if (id === "assessmentStatus") {
      setFormData((prev) => ({ ...prev, status: { name: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [id]: value }));
    }
  };

  // Handler for 'react-select' multi-select
  const handlePreparedByChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      preparedBy: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // *** FIX IS HERE: Restructure the flat form data into the nested object the backend expects ***
    const {
      // Destructure version-specific fields from the flat formData
      version,
      description,
      reportReleaseDate,
      preparedBy,
      reviewedBy,
      approvedBy,
      sdm,
      // The rest of the properties are for the main assessment object
      ...assessmentData
    } = formData;

    // Build the final payload with the correct nested structure
    const dataToSend = {
      ...assessmentData,
      assessmentStartDate: formData.assessmentStartDate || null,
      assessmentEndDate: formData.assessmentEndDate || null,
      versions: [
        {
          versionId: version, // Map 'version' state to 'versionId' field
          description,
          reportReleaseDate: reportReleaseDate || null,
          preparedBy,
          reviewedBy,
          approvedBy,
          sdm,
        },
      ],
    };
    // *** END OF FIX ***

    if (assessmentToEdit) {
      dataToSend.assessmentId = assessmentToEdit.assessmentId;
    }

    console.log("Submitting structured data:", dataToSend);
    const success = await onSave(dataToSend);

    if (success) {
      navigate("/dashboard");
    }
  };

  // Combine and deduplicate managers
  const allManagers = [...managers, ...assistantManagers];
  const uniqueManagers = Array.from(
    new Set(allManagers.map((u) => u.userId))
  ).map((id) => allManagers.find((u) => u.userId === id));

  // Format testers for react-select
  const testerOptions = testers.map((user) => ({
    value: user.name,
    label: user.name,
  }));

  return (
    <div className="container card">
      <div className="flex items-center space-x-4">
        <img src="/varutra-logo.png" height={50} alt="Varutra Logo" />
        <h1 className="text-1xl font-bold text-gray-800">
          {assessmentToEdit ? "Edit Assessment" : "Create New Assessment"}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client Name Dropdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center mb-6">
          <div className="md:col-span-1"></div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="clientName" className="form-label">
              Client Name
            </label>
            <select
              id="clientName"
              className="form-input"
              value={formData.clientName}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Client --</option>
              <option value="TechCorp Solutions">TechCorp Solutions</option>
              <option value="Global Innovations Inc.">
                Global Innovations Inc.
              </option>
              <option value="SecureNet Systems">SecureNet Systems</option>
              <option value="CyberSafe Ltd.">CyberSafe Ltd.</option>
              <option value="DataProtect Corp.">DataProtect Corp.</option>
              <option value="Fintech Innovations">Fintech Innovations</option>
              <option value="HealthCare Secure">HealthCare Secure</option>
              <option value="Retail Giant Corp">Retail Giant Corp</option>
            </select>
          </div>
        </div>

        {/* Activity, Scope, and other details */}
        <div className="form-grid">
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="activityName" className="form-label">
              Activity Name
            </label>
            <input
              type="text"
              id="activityName"
              className="form-input"
              placeholder="Enter Activity Name"
              value={formData.activityName}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="scopeDetails" className="form-label">
              Scope Details
            </label>
            <textarea
              id="scopeDetails"
              className="form-textarea h-24 resize-y"
              placeholder="Enter Scope Details"
              value={formData.scopeDetails}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="outOfScope" className="form-label">
              Out of Scope
            </label>
            <textarea
              id="outOfScope"
              className="form-textarea h-24 resize-y"
              placeholder="Enter Out of Scope Details"
              value={formData.outOfScope}
              onChange={handleChange}
            ></textarea>
          </div>
          {/* Dates */}
          <div className="form-group col-span-12 md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="assessmentStartDate" className="form-label">
                Assessment Start Date
              </label>
              <input
                type="date"
                id="assessmentStartDate"
                className="form-input"
                value={formData.assessmentStartDate}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="assessmentEndDate" className="form-label">
                Assessment End Date
              </label>
              <input
                type="date"
                id="assessmentEndDate"
                className="form-input"
                value={formData.assessmentEndDate}
                onChange={handleChange}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="reportReleaseDate" className="form-label">
                Report Release Date
              </label>
              <input
                type="date"
                id="reportReleaseDate"
                className="form-input"
                value={formData.reportReleaseDate}
                onChange={handleChange}
              />
            </div>
          </div>
          {/* Assessment Status */}
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="assessmentStatus" className="form-label">
              Assessment Status
            </label>
            {assessmentToEdit ? (
              <select
                id="assessmentStatus"
                className="form-input"
                value={formData.status.name}
                onChange={handleChange}
                required
              >
                <option value="">-- Select Status --</option>
                <option value="In Progress">In Progress</option>
                <option value="L1 Approved">L1 Approved</option>
                <option value="L2 Approved">L2 Approved</option>
                <option value="L1 Waiting">L1 Waiting</option>
                <option value="Closed">Closed</option>
              </select>
            ) : (
              <input
                type="text"
                id="assessmentStatus"
                className="form-input"
                value="In Progress"
                readOnly
                disabled
              />
            )}
          </div>
          {/* Team fields */}
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="preparedBy" className="form-label">
              Prepared by
            </label>
            <Select
              id="preparedBy"
              isMulti
              options={testerOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder="Select Testers"
              onChange={handlePreparedByChange}
              value={testerOptions.filter((option) =>
                formData.preparedBy.includes(option.value)
              )}
            />
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="reviewedBy" className="form-label">
              Reviewed by
            </label>
            <select
              id="reviewedBy"
              className="form-input"
              value={formData.reviewedBy}
              onChange={handleChange}
            >
              <option value="">-- Select Project Lead --</option>
              {teamLeads.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="approvedBy" className="form-label">
              Approved by
            </label>
            <select
              id="approvedBy"
              className="form-input"
              value={formData.approvedBy}
              onChange={handleChange}
            >
              <option value="">-- Select Manager --</option>
              {uniqueManagers.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name} {user.roles[0]?.role && `(${user.roles[0].role})`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="sdm" className="form-label">
              SDM
            </label>
            <select
              id="sdm"
              className="form-input"
              value={formData.sdm}
              onChange={handleChange}
            >
              <option value="">-- Select SDM --</option>
              {uniqueManagers.map((user) => (
                <option key={user.userId} value={user.name}>
                  {user.name} {user.roles[0]?.role && `(${user.roles[0].role})`}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="version" className="form-label">
              Version
            </label>
            <input
              type="text"
              id="version"
              className="form-input"
              value={formData.version}
              onChange={handleChange} // Allow editing version
            />
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              className="form-textarea h-24 resize-y"
              placeholder="Enter Description"
              value={formData.description}
              onChange={handleChange}
            ></textarea>
          </div>
          <div className="form-group col-span-12 md:col-span-6">
            <label htmlFor="privacy_of_content" className="form-label">
              Privacy of Content
            </label>
            <textarea
              id="privacy_of_content"
              className="form-textarea h-24 resize-y"
              value={formData.privacy_of_content}
              readOnly
            ></textarea>
          </div>
        </div>
        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </button>
          <button type="submit" className="btn-success">
            {assessmentToEdit ? "Update Assessment" : "Save Assessment"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewAssessmentForm;
